import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, Flex, Text, Circle, useBreakpointValue } from 'src/components/ui';
import { CheckIcon } from '@chakra-ui/icons';
import { CampaignContext } from 'src/contexts';
import {
	RIGHT_SIDEBAR_WIDTH,
	RIGHT_SIDEBAR_WIDTH_COLLAPSED,
	TOP_BAR_HEIGHT,
	LEFT_SIDEBAR_WIDTH,
} from './utils/constants';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';
import { useRightSidebar } from 'src/layout/main/right-sidebar/useRightSidebar';
import { useLeftSidebarContext } from 'src/contexts/app/LeftSidebarContext';

interface CampaignStepperProps {
	handleOpenSection: (index: number) => void | Promise<void>;
	designDirectionsRef: React.RefObject<HTMLDivElement>;
	scheduleRef: React.RefObject<HTMLDivElement>;
	scrollIntoRef: (
		ref: React.RefObject<HTMLDivElement>,
		offset?: number,
	) => void;
	currentOpenSectionIndex: number;
}

const CampaignStepper: React.FC<CampaignStepperProps> = ({
	handleOpenSection,
	scheduleRef,
	scrollIntoRef,
	currentOpenSectionIndex,
}) => {
	const { isSidebarOpen } = useRightSidebar();
	const { isOpen: isLeftSidebarOpen } = useLeftSidebarContext();
	const { steps, isEditorMode } = useContext(CampaignContext);
	const circleSize = useBreakpointValue({ base: '24px', md: '32px' });
	const fontSize = useBreakpointValue({ base: 'xs', md: 'sm' });
	const rightOffset = isSidebarOpen
		? RIGHT_SIDEBAR_WIDTH
		: RIGHT_SIDEBAR_WIDTH_COLLAPSED;
	
	// Calculate left offset - only apply on desktop when sidebar is open
	const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
	const leftOffset = isDesktop && isLeftSidebarOpen ? LEFT_SIDEBAR_WIDTH : '0px';

	// Scroll-based collapse state
	const [isCollapsed, setIsCollapsed] = useState(false);

	// Collapse stepper when chat/copilot opens on desktop
	useEffect(() => {
		const isDesktop = window.innerWidth >= 768;
		if (isDesktop && isSidebarOpen) {
			setIsCollapsed(true);
		} else if (isDesktop && !isSidebarOpen) {
			setIsCollapsed(false);
		}
	}, [isSidebarOpen]);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const isMobile = window.innerWidth < 768;

			// Only collapse on mobile (screen width < 768px) based on scroll
			if (isMobile) {
				// Show full size only when at top (scrollY <= 50)
				// Otherwise show collapsed
				if (currentScrollY <= 50) {
					if (isCollapsed) {
						setIsCollapsed(false);
					}
				} else {
					if (!isCollapsed) {
						setIsCollapsed(true);
					}
				}
			}
			// Note: On desktop, collapse state is controlled by isSidebarOpen (see useEffect above)
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, [isCollapsed]);

	const visibleSteps = steps.slice(0, 3);

	// Hide stepper when in editor mode
	if (isEditorMode) {
		return null;
	}

	return (
		<Box
			bg="white"
			p={{ base: isCollapsed ? 2 : 4, md: 2 }}
			pb={{ base: 0, md: 2 }}
			borderRadius="md"
			boxShadow="sm"
			position="fixed"
			top={TOP_BAR_HEIGHT}
			left={leftOffset}
			width={`calc(100vw - ${leftOffset} - ${rightOffset})`}
			zIndex={1850}
			transition="all 0.3s ease"
		>
			<Flex
				justifyContent="space-between"
				alignItems="center"
				mb={{ base: isCollapsed ? 2 : 4, md: 2 }}
				width="100%"
				maxW="1600px"
				mx="auto"
				px={{ base: isCollapsed ? 2 : 5, md: 5 }}
				transition="all 0.3s ease"
			>
				{visibleSteps.map((step, index) => {
					const isClickable =
						(step.isCompleted || step.isActive) &&
						index !== currentOpenSectionIndex;

					return (
						<React.Fragment key={index}>
							<Flex
								direction="column"
								alignItems="center"
								flex="1"
								cursor={isClickable ? 'pointer' : 'default'}
								onClick={() => {
									if (isClickable) {
										handleOpenSection(index);
										if (index === 0) {
											window.scrollTo({ top: 0, behavior: 'smooth' });
										} else if (index === 2) {
											scrollIntoRef(scheduleRef);
										}
									}
								}}
							>
								<Circle
									size={circleSize}
									bg={step.isCompleted ? '#F7480B' : 'white'}
									borderColor={
										step.isCompleted
											? '#F7480B'
											: step.isActive
												? '#F7480B'
												: 'gray.300'
									}
									borderWidth={2}
									color={
										step.isCompleted
											? 'white'
											: step.isActive
												? '#F7480B'
												: 'gray.500'
									}
								>
									{step.isCompleted ? <CheckIcon color="white" /> : index + 1}
								</Circle>
								{!isCollapsed && (
									<Text
										mt={2}
										fontSize={fontSize}
										fontWeight="medium"
										textAlign="center"
										minH={{ base: '32px', md: '24px' }}
										display="flex"
										alignItems="center"
										justifyContent="center"
										color={
											step.isCompleted
												? '#F7480B'
												: step.isActive
													? 'black'
													: 'gray.500'
										}
									>
										{step.label}
									</Text>
								)}
							</Flex>
							{index < visibleSteps.length - 1 && (
								<Box
									flex="1"
									height="2px"
									bg={steps[index].isCompleted ? '#F7480B' : 'gray.300'}
									alignSelf="center"
									mb={isCollapsed ? 0 : { base: '32px', md: '24px' }}
								/>
							)}
						</React.Fragment>
					);
				})}
			</Flex>
		</Box>
	);
};

export default CampaignStepper;
